import FirebaseServiceBase from "./FirebaseServiceBase"
import keys from "constants/keys"
import firebase from "firebase/app"

export default new (class DatabaseService extends FirebaseServiceBase {
  requestNotifications = () => {
    return this.messaging.requestPermission()
  }

  updateToken = async () => {
    this.messaging.onTokenRefresh(async () => {
      const token = await this.getToken()
      saveToken(token)
    })
  }

  getToken = () => {
    return this.messaging.getToken()
  }

  saveToken = token => {
    return this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid.toString())
      .update({
        messagingToken: token,
      })
  }

  saveSafariToken = token => {
    return this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid.toString())
      .update({
        safariToken: token,
      })
  }

  doCreateUser = async ({
    id,
    phone,
    instagram,
    type,
    email,
    picture,
    firstName,
    lastName
  }) => {
    let updateData = {}

    if (phone) {
      updateData.phone = phone
    }
    if (instagram) {
      updateData.instagram = instagram.substr(1)
    }
    if (type) {
      updateData.type = type
    }
    if (email) {
      updateData.email = email
    }
    if (picture) {
      updateData.picture = picture
    }
    if (firstName) {
      updateData.firstName = firstName
    }
    if (lastName) {
      updateData.lastName = lastName
    }
    const userRef = this.firestore.collection("users").doc(id)
    const user = await userRef.get()
    if (user.exists) {
      return userRef.update({
        ...updateData
      })
    }
    return userRef
      .set({
        ...updateData,
        createdAt: firebase.firestore.Timestamp.now(),
      })
  }

  /**
   * Pings the user endpoint.
   * Can be used as a quick way to wake up the firebase functions
   * so that they'll be faster for an upcoming action.
   */
  ping = async () => {
    await this.http(`/users/${this.auth.currentUser.uid}/hello`)
  }

  searchNumbersAvailable = async ({ areaCode, contains, inRegion, limit }) => {
    const response = await this.http(`/phone/numbers-available`, {
      method: "POST",
      body: JSON.stringify({
        areaCode,
        contains,
        inRegion,
        limit,
      }),
    })
    if (!response.ok) {
      throw new Error("Could not retrieve dashboard")
    }
    const result = await response.json()
    return result
  }

  addNumber = number => {
    const userRef = this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid.toString())
    return userRef.update({
      pendingNumber: number,
    })
  }

  updateName = (firstName, lastName) => {
    const userRef = this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid.toString())
    return userRef.update({
      firstName,
      lastName,
    })
  }

  addUserPhoto = photoUrl => {
    const userRef = this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid.toString())
    return userRef.update({
      picture: photoUrl,
    })
  }

  verifyEmail = () => {
    if (!this.auth.currentUser) {
      return
    }
    const userRef = this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid.toString())

    return userRef.update({
      emailVerified: true,
    })
  }

  getUsers = () => {
    return this.firestore
      .collection("users")
      .orderBy("createdAt", "desc")
      .get()
  }

  getUserRecievedMessages = () => {
    if (!this.auth.currentUser) return
    return this.firestore
      .collection("messages")
      .where("ittyId", "==", this.auth.currentUser.uid.toString())
      .limit(30)
      .orderBy("createdAt", "desc")
  }

  getThreads = id => {
    return this.firestore
      .collection("messages")
      .doc(id)
      .collection("threads")
      .orderBy("createdAt", "desc")
      .limit(30)
  }

  getThreadOffset = (id, startAfter) => {
    return this.firestore
      .collection("messages")
      .doc(id)
      .collection("threads")
      .orderBy("createdAt", "desc")
      .limit(30)
      .startAfter(startAfter)
      .get()
  }

  createMessage = async (id, data) => {
    let { message, messageID } = data

    return this.firestore
      .collection("messages")
      .doc(messageID)
      .collection("threads")
      .add({
        direction: 1,
        body: message,
      })
  }

  newMessage = phone => {
    const message_path = `${this.auth.currentUser.uid.toString()}_${phone}`

    return this.firestore
      .collection("messages")
      .doc(message_path)
      .collection("threads")
      .add({
        direction: 1,
        body: message,
      })
  }

  hasSeenMessage = (id, hasSeen=true) => {
    return this.firestore
      .collection("messages")
      .doc(id)
      .update({ hasSeen })
  }

  blockNumber = async (num, add) => {
    const message_path = `${this.auth.currentUser.uid.toString()}_${num}`
    var updateMessage
    var blocked
    if (!add) {
      blocked = firebase.firestore.FieldValue.arrayRemove(num)
      updateMessage = await this.firestore
        .collection("messages")
        .doc(message_path)
        .update({ blocked: false })
    } else {
      blocked = firebase.firestore.FieldValue.arrayUnion(num)
      updateMessage = await this.firestore
        .collection("messages")
        .doc(message_path)
        .update({ blocked: true })
    }
    return this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid)
      .update({
        blocked,
      })
  }

  getContacts = () => {
    return this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid)
      .collection("contacts")
      .limit(150)
      .get()
  }

  fetchContact = id => {
    return this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid)
      .collection("contacts")
      .doc(id)
      .get()
  }

  updateContact = ({ id, firstName, lastName }) => {
    let updateData = {}

    if (firstName) {
      updateData.firstName = firstName
    }
    if (lastName) {
      updateData.lastName = lastName
    }

    return this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid)
      .collection("contacts")
      .doc(id)
      .set({
        ...updateData,
      })
  }

  /**
   * Returns the current user's doc ref
   * @returns {firebase.Firestore.DocumentReference}
   */
  get currentUser() {
    if (!this.auth.currentUser) {
      return null
    }
    return this.firestore.collection("users").doc(this.auth.currentUser.uid)
  }

  getCurrentUser = () => {
    if (!this.auth.currentUser) {
      return
    }
    return this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid.toString())
      .get()
  }

  addUserPaymentSource = async (source, startSubscription) => {
    if (this.currentUser === null) {
      return
    }
    return await this.http(`/users/${this.auth.currentUser.uid}/payment/sources`, {
      method: "POST",
      body: JSON.stringify({
        source,
        startSubscription: true
      }),
    })
  }

  cancelSubscription = async () => {
    return await this.http(`/users/${this.auth.currentUser.uid}/subscription/cancel`, {
      method: "POST"
    })
  }

  activateSubscription = async () => {
    return await this.http(`/users/${this.auth.currentUser.uid}/subscription/activate`, {
      method: "POST"
    })
  }

  updateCurrentUser = data => {
    let updateData = {}
    const userRef = this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid.toString())
    if (data.firstName) {
      updateData.firstName = data.firstName
    }
    if (data.lastName) {
      updateData.lastName = data.lastName
    }
    if (data.picture) {
      updateData.picture = data.picture
    }
    if (data.bio) {
      updateData.bio = data.bio
    }
    if (data.youtube != null) {
      updateData.youtubeURL = data.youtube
    }
    if (data.instagram != null) {
      updateData.instagramURL = data.instagram
    }
    if (data.city) {
      updateData.city = data.city
    }
    if (data.phone) {
      updateData.phone = data.phone
    }
    return userRef.update({
      ...updateData,
    })
  }

  getUserById = id => {
    return this.firestore
      .collection("users")
      .doc(id.toString())
      .get()
  }

  getUserByHandle = handle => {
    return this.firestore
      .collection("users")
      .where("instagram", "==", handle)
      .get()
  }

  isFollowingId = id => {
    return this.firestore
      .collection("relationships")
      .doc(`${this.auth.currentUser.uid.toString()}_${id}`)
      .get()
  }

  getCurrentUserFollowing = () => {
    return this.firestore
      .collection("relationships")
      .where("followerId", "==", this.auth.currentUser.uid.toString())
      .get()
  }

  getCurrentUserFollowers = () => {
    return this.firestore
      .collection("relationships")
      .where("followedId", "==", this.auth.currentUser.uid.toString())
      .get()
  }

  getFollowers = number => {
    return this.firestore
      .collection("relationships")
      .where("followedNumber", "==", number)
      .get()
  }

  // *** Message API ***

  getUserSentMessages = number => {
    if (!number) {
      return {}
    }
    return this.firestore
      .collection("messages")
      .where("from", "==", number)
      .limit(30)
      .get()
  }

  getMessageById = id => {
    return this.firestore
      .collection("messages")
      .doc(id.toString())
      .get()
  }

  getCurrentUserItems = () => {
    const userRef = this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid.toString())
    return this.firestore
      .collection("fleet")
      .where("user", "==", userRef)
      .where("deleted", "==", false)
      .get()
  }

  getDashboard = async () => {
    const response = await this.http(
      `/users/${this.auth.currentUser.uid.toString()}/getStripeDashboard`,
      {
        method: "POST",
      }
    )
    if (!response.ok) {
      throw new Error("Could not retrieve dashboard")
    }
    const result = await response.json()
    return result
  }

  createSubscription = async data => {
    const { followID, source } = data

    const customerID = this.auth.currentUser.uid.toString()

    const response = await this.http("/subscriptions", {
      method: "POST",
      body: JSON.stringify({
        source,
        customerID,
        followID,
      }),
    })
    if (!response.ok) {
      throw new Error("Could not create rental")
    }
    const result = await response.json()
    return result
  }

  updateSubscribe = ({ type, subscribe }) => {
    let updateVal = {}
    if (type == "email") {
      updateVal = { newsletterSubscribed: subscribe }
    } else if (type == "text") {
      updateVal = { textSubscribed: subscribe }
    }
    const userRef = this.firestore
      .collection("users")
      .doc(this.auth.currentUser.uid.toString())
    return userRef.update(updateVal)
  }
})()
